/**
 * Workspace Tab Bar - Zendesk-style ticket tabs (global header)
 */

import React from 'react';
import { Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAgentWorkspaceTabs } from '../contexts/AgentWorkspaceTabsContext';

interface WorkspaceTabBarProps {
  compact?: boolean;
}

export const WorkspaceTabBar: React.FC<WorkspaceTabBarProps> = ({ compact }) => {
  const navigate = useNavigate();
  const ctx = useAgentWorkspaceTabs();
  if (!ctx) return null;

  const { tabs, activeTabId, removeTab, switchToTab } = ctx;
  if (tabs.length < 1) return null;

  const handleClose = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    const idx = tabs.findIndex((t) => t.id === tabId);
    const nextTab = idx > 0 ? tabs[idx - 1] : tabs[idx + 1];
    removeTab(tabId);
    if (tabId === activeTabId) {
      if (nextTab) switchToTab(nextTab.id);
      else navigate('/tickets');
    }
  };

  const pad = compact ? '4px 8px' : '8px 12px';
  const minW = compact ? 60 : 80;
  const maxW = compact ? 120 : 180;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        flexWrap: 'wrap',
        ...(compact ? {} : { marginBottom: 16, borderBottom: '1px solid #E7E0EC', backgroundColor: '#fafafa', borderRadius: 8, padding: '4px 8px 0' }),
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeTabId;
        return (
          <div
            key={tab.id}
            onClick={() => switchToTab(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: pad,
              cursor: 'pointer',
              borderRadius: 6,
              backgroundColor: isActive ? '#fff' : 'transparent',
              borderBottom: isActive ? '2px solid #1890ff' : '2px solid transparent',
              marginBottom: compact ? 0 : -1,
              minWidth: minW,
              maxWidth: maxW,
              boxShadow: compact && isActive ? '0 1px 2px rgba(0,0,0,0.06)' : undefined,
            }}
          >
              <Typography.Text
                strong={isActive}
                ellipsis
                style={{ fontSize: compact ? 12 : 13, flex: 1, minWidth: 0 }}
              >
                {tab.label}
              </Typography.Text>
              <span
                onClick={(e) => handleClose(e, tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 18,
                  height: 18,
                  borderRadius: 4,
                  cursor: 'pointer',
                  color: '#666',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#e0e0e0';
                  (e.currentTarget as HTMLElement).style.color = '#333';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = '#666';
                }}
              >
                <CloseOutlined style={{ fontSize: 10 }} />
              </span>
            </div>
          );
        })}
    </div>
  );
};
